import React from 'react';
import type { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox, LabelInput } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { FormLayout } from '@/components/layout';
import type { CreateEmployeePersonalData } from '@/schemas/employee/createEmployeeForm';
import {
  normalizeSelectValue,
  normalizeSelectToNumber,
  normalizeSelectWithFallback,
  normalizeWatchValue,
  normalizeWatchValueToString,
} from '@/utils/formHelpers';


interface CreateEmployeePersonalDataFormProps {
  personalForm: {
    register: UseFormRegister<CreateEmployeePersonalData>;
    setValue: UseFormSetValue<CreateEmployeePersonalData>;
    watch: UseFormWatch<CreateEmployeePersonalData>;
    errors: FieldErrors<CreateEmployeePersonalData>;
    trigger: UseFormTrigger<CreateEmployeePersonalData>;
  };
  genderOptions: { value: string; label: string }[];
  maritalStatusOptions: { value: string; label: string }[];
  employmentStatusOptions: { value: string; label: string }[];
  usernameSuffix: string | null;
  generatedUsername: string;
  syncAssistance?: boolean;
  onCancel: () => void;
  onNext?: () => void;
}

export const CreateEmployeePersonalDataForm: React.FC<
  CreateEmployeePersonalDataFormProps
> = ({
  personalForm,
  genderOptions,
  maritalStatusOptions,
  employmentStatusOptions,
  usernameSuffix: _usernameSuffix,
  generatedUsername,
  syncAssistance = false,
  onCancel,
  onNext,
}) => {
  const { t: translate } = useTranslation();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  // Format RUT while typing: remove dots, allow only digits, hyphen, and K/k
  const handleRutChange = (value: string) => {
    // Remove dots and spaces
    let cleaned = value.replace(/\./g, '').replace(/\s/g, '');

    // Convert to uppercase for consistency
    cleaned = cleaned.toUpperCase();

    // Only allow digits, hyphen, and K
    cleaned = cleaned.replace(/[^0-9-K]/g, '');

    // Remove all hyphens first to normalize
    const withoutHyphens = cleaned.replace(/-/g, '');

    // If empty or only one character, don't format
    if (withoutHyphens.length <= 1) {
      personalForm.setValue('rutComplete', withoutHyphens, { shouldDirty: true });
      return;
    }

    // Auto-format: add hyphen before last character
    // Split into body (all but last char) and DV (last char)
    const body = withoutHyphens.slice(0, -1);
    const dv = withoutHyphens.slice(-1);
    const formatted = `${body}-${dv}`;

    personalForm.setValue('rutComplete', formatted, { shouldDirty: true });
  };

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      <FormLayout.Section title="Datos personales del nuevo colaborador">
        {/* Row 1: Nombre de usuario, RUT, Fecha de nacimiento, Empleado no sincroniza */}
        <LabelInput
          label="Nombre de usuario"
          value={generatedUsername}
          onChange={() => {}}
          disabled
          placeholder="Se generará automáticamente desde el RUT"
        />
        <LabelInput
          label="RUT"
          value={personalForm.watch('rutComplete')}
          onChange={handleRutChange}
          placeholder="12345678-9"
          required
          color={personalForm.errors.rutComplete ? 'error' : undefined}
          supportingText={personalForm.errors.rutComplete?.message}
        />
        <DatePicker
          label="Fecha de nacimiento"
          value={normalizeWatchValue(personalForm.watch('birthDate'))}
          onChange={(value) => personalForm.setValue('birthDate', normalizeWatchValueToString(value), { shouldDirty: true })}
          placeholder="dd/mm/aaaa"
          color={personalForm.errors.birthDate ? 'error' : undefined}
          supportingText={personalForm.errors.birthDate?.message}
        />
        {syncAssistance ? (
          <Checkbox
            label={translate('employee:fields.doesNotSync')}
            tooltipText={translate('employee:fields.doesNotSyncTooltip')}
            checked={!!personalForm.watch('doesNotSync')}
            onChange={(checked) => personalForm.setValue('doesNotSync', checked, { shouldDirty: true })}
            data-testid="empleado-no-sincroniza-checkbox"
          />
        ) : (
          <div aria-hidden="true" className="hidden md:block" />
        )}

        {/* Row 2: Nombres, Apellido paterno, Apellido materno, Sexo */}
        <LabelInput
          label="Nombres"
          value={personalForm.watch('firstNames')}
          onChange={(value) => personalForm.setValue('firstNames', value, { shouldDirty: true })}
          required
          color={personalForm.errors.firstNames ? 'error' : undefined}
          supportingText={personalForm.errors.firstNames?.message}
          data-testid="firstNames-input"
        />
        <LabelInput
          label="Apellido paterno"
          value={personalForm.watch('lastName')}
          onChange={(value) => personalForm.setValue('lastName', value, { shouldDirty: true })}
          required
          color={personalForm.errors.lastName ? 'error' : undefined}
          supportingText={personalForm.errors.lastName?.message}
          data-testid="lastName-input"
        />
        <LabelInput
          label="Apellido materno"
          value={personalForm.watch('secondLastName')}
          onChange={(value) => personalForm.setValue('secondLastName', value, { shouldDirty: true })}
          color={personalForm.errors.secondLastName ? 'error' : undefined}
          supportingText={personalForm.errors.secondLastName?.message}
          data-testid="secondLastName-input"
        />

        <Selector
          label="Sexo"
          value={normalizeWatchValue(personalForm.watch('genderCode'))}
          onChange={(value) => personalForm.setValue('genderCode', normalizeSelectValue(value), { shouldDirty: true })}
          options={genderOptions}
          placeholder="Selecciona sexo"
          required
          color={personalForm.errors.genderCode ? 'error' : undefined}
          supportingText={personalForm.errors.genderCode?.message}
        />

        {/* Row 3: Estado civil, Estado del empleado */}
        <Selector
          label="Estado civil"
          value={personalForm.watch('maritalStatusId')
            ? String(personalForm.watch('maritalStatusId'))
            : undefined}
          onChange={(value) => {
            personalForm.setValue('maritalStatusId', normalizeSelectToNumber(value), { shouldDirty: true });
          }}
          options={maritalStatusOptions}
          placeholder="Selecciona estado civil"
          required
          color={personalForm.errors.maritalStatusId ? 'error' : undefined}
          supportingText={personalForm.errors.maritalStatusId?.message}
        />
        <Selector
          label={translate('employee:fields.statusProfile')}
          value={normalizeSelectWithFallback(personalForm.watch('employmentStatus'), 'V')}
          onChange={(value) => personalForm.setValue('employmentStatus', normalizeSelectWithFallback(value, 'V'), { shouldDirty: true })}
          options={employmentStatusOptions}
          placeholder={translate('employee:fields.statusProfilePlaceholder')}
          required
          color={personalForm.errors.employmentStatus ? 'error' : undefined}
          supportingText={personalForm.errors.employmentStatus?.message}
        />
      </FormLayout.Section>

      <FormLayout.Footer
        cancelButton={
          <PrimaryButton type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </PrimaryButton>
        }
        primaryButton={
          onNext ? (
            <PrimaryButton 
              type="button" 
              onClick={async () => {
                const isValid = await personalForm.trigger();
                if (isValid) onNext();
              }}
            >
              Siguiente
            </PrimaryButton>
          ) : undefined
        }
      />
    </FormLayout>
  );
};

export default CreateEmployeePersonalDataForm;
